// apiErrors.js
//
// Classifying API failures, in one place.
//
// Several screens need to tell "you are out of credits" apart from "something
// broke". Getting that wrong is not cosmetic: a credit limit should offer a way
// forward — sign in, or upgrade — while a genuine failure should offer a retry.
// Treating the first as the second produces a retry loop against an endpoint
// that will keep refusing.

/**
 * Codes the API uses when a request is refused for want of credit, rather than
 * because anything went wrong.
 */
const CREDIT_LIMIT_CODES = new Set([
  'anonymous_limit_reached',
  'insufficient_credits',
  'usage_limit_reached',
]);

const responseData = (error) => error?.response?.data ?? {};

/**
 * True when the request was refused because the caller has no credit left.
 *
 * Matches on the error code first, which is the API's own answer. Status is a
 * fallback: 402 is what the service returns, and 429 is accepted because it is
 * what the previous API used for the same condition, so an older client or a
 * cached bundle still behaves.
 */
export const isCreditLimitError = (error) => {
  const status = error?.response?.status;
  const { code } = responseData(error);

  if (code && CREDIT_LIMIT_CODES.has(code)) return true;
  return status === 402 || status === 429;
};

/**
 * True when the feature itself needs a paid plan, regardless of credit.
 *
 * 403 counts: the API uses it for features that need a paid plan. The message
 * check is kept because the API words these refusals with "premium", and
 * several screens keyed off that long before there were error codes.
 */
export const isPremiumRequiredError = (error) => {
  const { code, error: message } = responseData(error);

  if (code === 'video_requires_pro' || code === 'premium_required') return true;
  if (error?.response?.status === 403) return true;
  return typeof message === 'string' && message.includes('premium');
};

/**
 * True when the request should not be retried, because repeating it cannot
 * change the answer.
 */
export const isTerminalError = (error) =>
  isCreditLimitError(error) || isPremiumRequiredError(error);

/** The message the API sent, if it sent one. */
export const apiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const { error: message } = responseData(error);
  return typeof message === 'string' && message ? message : error?.message || fallback;
};
